(() => {
  const DATA = window.TPS_RACKET_FINDER_DATA || {};
  const catalog = Array.isArray(DATA.rackets) ? DATA.rackets : [];

  const questions = [
    {
      key: "experience",
      title: "What experience are you bringing?",
      help: "Choose the option that best describes your current starting point.",
      options: [
        ["new", "Completely new", "I am new to padel and racket sports", "☺"],
        ["transfer", "New to padel", "I have played tennis, squash, badminton or a similar sport", "↔"],
        ["padel", "Some padel experience", "I know the basics and play occasionally", "◉"],
        ["established", "Established padel player", "I understand my game and play confidently", "◆"]
      ]
    },
    {
      key: "style",
      title: "Which sounds most like your game?",
      help: "Think about how you naturally play, or how you expect to play.",
      options: [
        ["defensive", "Keep the ball in play", "Control, defence and making fewer mistakes", "◎"],
        ["all-rounder", "A bit of everything", "Adapt between patient defence and controlled attack", "◇"],
        ["attacking", "Take the initiative", "Move forward, volley and look to finish points", "⚡"],
        ["any", "Too new to know", "Use my other answers to guide the result", "?"]
      ]
    },
    {
      key: "goal",
      title: "What do you want most from your racket?",
      help: "Choose the change that would make the greatest difference to your game.",
      options: [
        ["control", "Control and confidence", "Cleaner contact and easier placement", "◎"],
        ["control-power", "Control with useful power", "Security in defence with help in attack", "◇"],
        ["power", "More attacking power", "More weight through volleys and overheads", "⚡"],
        ["any", "I am not sure", "Use my other answers to decide", "?"]
      ]
    },
    {
      key: "comfort",
      title: "How important is arm comfort?",
      help: "This helps us avoid a racket that feels too demanding over a full match.",
      options: [
        ["priority", "Comfort is essential", "I have elbow or shoulder sensitivity, or want the safest setup", "♡"],
        ["helpful", "I prefer extra comfort", "I do not want a harsh or tiring response", "○"],
        ["open", "No current concerns", "I am comfortable with a more direct response", "◆"],
        ["any", "I am not sure", "Use my experience and preferences to guide this", "?"]
      ]
    },
    {
      key: "handling",
      title: "How should it feel in your hand?",
      help: "No technical knowledge needed. Choose what sounds most comfortable.",
      options: [
        ["light", "Light and quick", "Easy to move and prepare for the next shot", "↗"],
        ["balanced", "Balanced and versatile", "A blend of manoeuvrability and stability", "◇"],
        ["solid", "Solid and steady", "More stability when the ball arrives with pace", "▣"],
        ["any", "I am not sure", "Let my other answers decide", "?"]
      ]
    }
  ];

  function finderTagValues(tags, key) {
    const prefix = `finder:${key}:`;
    return (tags || [])
      .filter(tag => String(tag).toLowerCase().startsWith(prefix))
      .map(tag => String(tag).slice(prefix.length).trim().toLowerCase())
      .filter(Boolean);
  }

  function technicalProduct(product) {
    if (!product) return null;
    const existing = catalog.find(racket => racket.handle === product.handle);

    if (existing) {
      return {
        ...existing,
        name: product.name || existing.name,
        handle: product.handle,
        image: product.image || existing.image,
        url: product.url || existing.url,
        price: Number(product.price || 0),
        compareAtPrice: Number(product.compareAtPrice || 0),
        available: Boolean(product.available)
      };
    }

    const attributes = {
      level: finderTagValues(product.tags, "level")[0],
      style: finderTagValues(product.tags, "style")[0],
      play: finderTagValues(product.tags, "play")[0],
      weight: finderTagValues(product.tags, "weight")[0],
      balance: finderTagValues(product.tags, "balance")[0],
      shape: finderTagValues(product.tags, "shape")[0],
      feel: finderTagValues(product.tags, "feel")[0]
    };

    if (Object.values(attributes).some(value => !value)) return null;

    return {
      ...attributes,
      name: product.name,
      handle: product.handle,
      image: product.image,
      url: product.url || `/products/${product.handle}`,
      price: Number(product.price || 0),
      compareAtPrice: Number(product.compareAtPrice || 0),
      available: Boolean(product.available),
      comfortTech: finderTagValues(product.tags, "comfort-tech")[0] === "true"
    };
  }

  function scoreRacket(racket, answers) {
    let score = 0;
    const levelScores = {
      new: { developing: 44, intermediate: 5, advanced: -26 },
      transfer: { developing: 29, intermediate: 31, advanced: -10 },
      padel: { developing: 10, intermediate: 39, advanced: 7 },
      established: { developing: -15, intermediate: 18, advanced: 40 }
    };
    score += levelScores[answers.experience]?.[racket.level] || 0;

    if (answers.style !== "any") {
      if (racket.style === answers.style) score += 24;
      else if (racket.style === "all-rounder" || answers.style === "all-rounder") score += 8;
      else score -= 5;
    }

    if (answers.goal !== "any") {
      const order = ["control", "control-power", "power"];
      if (racket.play === answers.goal) score += 27;
      else if (Math.abs(order.indexOf(racket.play) - order.indexOf(answers.goal)) === 1) score += 8;
      else score -= 8;
    }

    if (answers.comfort === "priority") {
      if (racket.feel === "soft") score += 19;
      if (racket.feel === "firm") score -= 21;
      if (racket.balance === "head-heavy") score -= 11;
      if (racket.weight === "heavy") score -= 13;
      if (racket.comfortTech) score += 13;
    } else if (answers.comfort === "helpful") {
      if (racket.feel === "soft") score += 9;
      if (racket.feel === "firm") score -= 9;
      if (racket.weight === "heavy") score -= 6;
      if (racket.comfortTech) score += 6;
    }

    if (answers.handling === "light") {
      if (racket.weight === "super-light") score += 21;
      if (racket.weight === "light") score += 18;
      if (racket.weight === "heavy") score -= 13;
      if (racket.balance === "head-light") score += 9;
    } else if (answers.handling === "balanced") {
      if (racket.weight === "medium") score += 13;
      if (racket.balance === "even") score += 12;
      if (racket.shape === "teardrop") score += 5;
    } else if (answers.handling === "solid") {
      if (["medium", "heavy"].includes(racket.weight)) score += 13;
      if (["even", "head-heavy"].includes(racket.balance)) score += 7;
      if (racket.feel === "medium") score += 7;
      if (racket.feel === "firm" && answers.experience !== "new") score += 5;
    }

    if (answers.experience === "new") {
      if (racket.balance === "head-heavy") score -= 13;
      if (["diamond", "geometric"].includes(racket.shape)) score -= 12;
      if (racket.feel === "firm") score -= 14;
      if (racket.weight === "heavy") score -= 15;
    }

    return score;
  }

  function alignmentDetails(racket, answers) {
    const positives = [];
    const tradeoffs = [];
    let severe = false;

    const expectedLevels = {
      new: ["developing"],
      transfer: ["developing", "intermediate"],
      padel: ["intermediate"],
      established: ["intermediate", "advanced"]
    };

    if (expectedLevels[answers.experience]?.includes(racket.level)) {
      positives.push("Its intended playing level aligns with your current experience.");
    } else if (answers.experience === "new" && racket.level === "advanced") {
      tradeoffs.push("It is designed for advanced technique and may be demanding while you learn padel.");
      severe = true;
    } else {
      tradeoffs.push("Its intended playing level is not the closest match to your experience.");
    }

    if (answers.style === "any" || racket.style === answers.style || racket.style === "all-rounder") {
      positives.push("Its playing style should adapt well to how you want to build points.");
    } else {
      tradeoffs.push("Its natural playing style differs from the way you expect to play.");
    }

    if (answers.goal === "any" || racket.play === answers.goal) {
      positives.push("Its control and power profile matches what you want from the racket.");
    } else if (
      (answers.goal === "control" && racket.play === "control-power") ||
      (answers.goal === "power" && racket.play === "control-power") ||
      (answers.goal === "control-power")
    ) {
      positives.push("Its control and power balance is close to your chosen priority.");
    } else {
      tradeoffs.push("Its control and power emphasis is different from your main priority.");
    }

    if (answers.comfort === "priority") {
      if (racket.feel === "soft" || racket.comfortTech) {
        positives.push("Its response includes the comfort support you prioritised.");
      } else if (racket.feel === "firm") {
        tradeoffs.push("Its firmer response may be too demanding if arm comfort is essential.");
        severe = true;
      } else {
        tradeoffs.push("It is not the most comfortable option for an arm sensitive player.");
      }
    } else if (answers.comfort === "helpful" && racket.feel === "firm") {
      tradeoffs.push("Its direct feel may be firmer than you prefer over a full match.");
    } else {
      positives.push("Its contact feel is compatible with your comfort preference.");
    }

    if (answers.handling === "light") {
      if (["super-light", "light"].includes(racket.weight) || racket.balance === "head-light") {
        positives.push("Its handling should feel quick and easy to prepare.");
      } else if (racket.weight === "heavy" || racket.balance === "head-heavy") {
        tradeoffs.push("It may feel slower and more demanding than the quick handling you selected.");
        if (answers.experience === "new") severe = true;
      }
    }

    if (answers.handling === "balanced") {
      if (racket.weight === "medium" || racket.balance === "even") {
        positives.push("Its weight distribution supports balanced all court handling.");
      } else {
        tradeoffs.push("Its handling is more specialised than the balanced feel you selected.");
      }
    }

    if (answers.handling === "solid") {
      if (["medium", "heavy"].includes(racket.weight) && racket.balance !== "head-light") {
        positives.push("Its mass and balance should provide the steadiness you want.");
      } else {
        tradeoffs.push("It may feel lighter and less planted than your preferred response.");
      }
    }

    if (answers.experience === "new") {
      const demanding = [
        racket.balance === "head-heavy",
        racket.weight === "heavy",
        racket.feel === "firm",
        ["diamond", "geometric"].includes(racket.shape)
      ].filter(Boolean).length;
      if (demanding >= 2) {
        severe = true;
        if (!tradeoffs.some(item => item.includes("demanding"))) {
          tradeoffs.push("Its weight, balance or shape may ask too much while you are developing clean technique.");
        }
      }
    }

    return {
      positives: [...new Set(positives)].slice(0, 3),
      tradeoffs: [...new Set(tradeoffs)].slice(0, 3),
      severe
    };
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: "NZD",
      maximumFractionDigits: 0
    }).format(cents / 100);
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function productImage(racket, className) {
    return racket.image
      ? `<img src="${escapeHtml(racket.image)}" alt="${escapeHtml(racket.name)}" loading="lazy">`
      : `<span class="image-placeholder">Product image</span>`;
  }

  function productPrice(racket) {
    const compare = racket.compareAtPrice > racket.price
      ? `<span class="compare-price">${formatMoney(racket.compareAtPrice)}</span>`
      : "";
    return `${formatMoney(racket.price)}${compare}`;
  }

  function track(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent("tps:fit-check", {
      detail: { event: eventName, ...detail }
    }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: eventName, ...detail });
    }
  }

  class ThePadelShopFitCheck extends HTMLElement {
    connectedCallback() {
      if (this.shadowRoot) return;

      let currentProductData = null;
      let collectionProducts = [];
      try {
        currentProductData = JSON.parse(this.querySelector("[data-tps-current-product]")?.textContent || "null");
        collectionProducts = JSON.parse(this.querySelector("[data-tps-fit-products]")?.textContent || "[]");
      } catch {
        currentProductData = null;
        collectionProducts = [];
      }

      const template = this.querySelector("template");
      if (!template) return;

      const root = this.attachShadow({ mode: "open" });
      root.appendChild(template.content.cloneNode(true));

      const currentRacket = technicalProduct(currentProductData);
      if (!currentRacket) {
        if (window.Shopify?.designMode) {
          root.querySelector(".fit-trigger").innerHTML = `<div class="setup-message">This product needs complete Racket Finder attributes before Check your fit can appear.</div>`;
        } else {
          this.hidden = true;
        }
        return;
      }

      const alternatives = collectionProducts
        .map(technicalProduct)
        .filter(racket => racket && racket.available && racket.handle !== currentRacket.handle);

      this.initialise(root, currentRacket, alternatives);
    }

    initialise(root, currentRacket, alternatives) {
      const dialog = root.querySelector("#fitDialog");
      const openButton = root.querySelector("#fitOpen");
      const closeButton = root.querySelector("#fitClose");
      const backButton = root.querySelector("#fitBack");
      const stepLabel = root.querySelector("#fitStepLabel");
      const progressBar = root.querySelector("#fitProgressBar");
      const host = root.querySelector("#fitHost");
      const fullFinderUrl = this.dataset.fullFinderUrl || "/pages/racket-finder";

      let currentQuestion = 0;
      let answers = {};
      let completed = false;

      const openDialog = () => {
        if (typeof dialog.showModal === "function") dialog.showModal();
        else dialog.setAttribute("open", "");
        track("tps_fit_check_open", { product_handle: currentRacket.handle });
      };

      const closeDialog = () => {
        if (typeof dialog.close === "function") dialog.close();
        else dialog.removeAttribute("open");
        openButton.focus();
      };

      const renderQuestion = () => {
        completed = false;
        const question = questions[currentQuestion];
        stepLabel.textContent = `Question ${currentQuestion + 1} of ${questions.length}`;
        progressBar.style.width = `${((currentQuestion + 1) / questions.length) * 100}%`;
        backButton.hidden = currentQuestion === 0;
        host.innerHTML = `
          <div class="fit-question">
            <h3>${question.title}</h3>
            <p class="fit-help">${question.help}</p>
            <div class="fit-options">
              ${question.options.map(([value, label, detail, icon]) => `
                <button type="button" class="fit-option" data-value="${value}">
                  <span class="fit-option-icon" aria-hidden="true">${icon}</span>
                  <span class="fit-option-copy"><strong>${label}</strong><small>${detail}</small></span>
                  <span class="fit-option-arrow" aria-hidden="true">›</span>
                </button>`).join("")}
            </div>
          </div>`;

        host.querySelectorAll("[data-value]").forEach(button => {
          button.addEventListener("click", () => {
            answers[question.key] = button.dataset.value;
            if (currentQuestion < questions.length - 1) {
              currentQuestion += 1;
              renderQuestion();
            } else {
              renderResult();
            }
          });
        });
      };

      const chooseAlternatives = (currentScore, assessment) => {
        const ranked = alternatives
          .map(racket => ({ ...racket, fitScore: scoreRacket(racket, answers) }))
          .sort((a, b) => b.fitScore - a.fitScore || a.price - b.price);

        const meaningfullyBetter = ranked.filter(racket => racket.fitScore >= currentScore + 5);
        const samePriceBand = meaningfullyBetter.filter(racket => racket.price <= currentRacket.price * 1.15);
        const preferred = samePriceBand.length ? samePriceBand : meaningfullyBetter;
        const pool = assessment === "excellent" ? [] : preferred;
        return pool.slice(0, assessment === "better" ? 2 : 1);
      };

      const alternativeCard = racket => `
        <article class="alternative-card">
          <div class="alternative-visual">${productImage(racket, "alternative")}</div>
          <div class="alternative-copy">
            <h4>${escapeHtml(racket.name)}</h4>
            <p class="fit-price">${productPrice(racket)}</p>
            <p class="alternative-reason">This option aligns more closely with your answers while remaining suitable for your current experience.</p>
            <a class="alternative-link" href="${escapeHtml(racket.url)}" data-alt-handle="${escapeHtml(racket.handle)}">View racket <span aria-hidden="true">→</span></a>
          </div>
        </article>`;

      const renderResult = () => {
        completed = true;
        stepLabel.textContent = "Your fit check";
        progressBar.style.width = "100%";
        backButton.hidden = true;

        const currentScore = scoreRacket(currentRacket, answers);
        const details = alignmentDetails(currentRacket, answers);
        const rankedAlternative = alternatives
          .map(racket => ({ ...racket, fitScore: scoreRacket(racket, answers) }))
          .sort((a, b) => b.fitScore - a.fitScore || a.price - b.price)[0];
        const scoreGap = rankedAlternative ? rankedAlternative.fitScore - currentScore : 0;

        let assessment = "excellent";
        if (details.severe || scoreGap >= 18) assessment = "better";
        else if (details.tradeoffs.length || scoreGap >= 8) assessment = "good";

        const resultCopy = {
          excellent: {
            status: "EXCELLENT FIT",
            title: "This racket looks like a strong fit for you.",
            lead: "Its intended player, response and handling align well with the answers you selected."
          },
          good: {
            status: "GOOD FIT WITH A TRADEOFF",
            title: "This racket can work well, with one thing to consider.",
            lead: "The overall fit is positive, but understanding the tradeoff will help you make a confident decision."
          },
          better: {
            status: "CONSIDER A BETTER MATCH",
            title: "We think another racket may suit you better.",
            lead: "This racket has one or more demanding characteristics that do not align closely with your answers."
          }
        }[assessment];

        const suggested = chooseAlternatives(currentScore, assessment);
        const positives = details.positives.length
          ? details.positives.join(" ")
          : "Some of its characteristics align with your selected playing priorities.";
        const tradeoffs = details.tradeoffs.length
          ? details.tradeoffs.join(" ")
          : "No important tradeoffs were identified from your answers.";

        host.innerHTML = `
          <div class="fit-result">
            <span class="fit-result-status">${resultCopy.status}</span>
            <h3>${resultCopy.title}</h3>
            <p class="fit-result-lead">${resultCopy.lead}</p>

            <div class="current-fit">
              <div class="current-fit-visual">${productImage(currentRacket, "current")}</div>
              <div class="current-fit-copy">
                <h4>${escapeHtml(currentRacket.name)}</h4>
                <p class="fit-price">${productPrice(currentRacket)}</p>
                <div class="alignment-grid">
                  <div class="alignment-panel"><strong>Why it fits</strong><p>${positives}</p></div>
                  <div class="alignment-panel"><strong>What to consider</strong><p>${tradeoffs}</p></div>
                </div>
              </div>
            </div>

            ${suggested.length ? `
              <section class="alternative-section">
                <h4>${assessment === "better" ? "Better aligned options" : "A useful alternative"}</h4>
                <div class="alternative-grid">${suggested.map(alternativeCard).join("")}</div>
              </section>` : ""}

            <div class="fit-actions">
              <button type="button" class="fit-primary" id="fitContinue">Continue with this racket</button>
              <button type="button" class="fit-secondary" id="fitRestart">Start again</button>
              <a class="fit-secondary" href="${escapeHtml(fullFinderUrl)}">Take the full Racket Finder</a>
            </div>
          </div>`;

        track("tps_fit_check_complete", {
          product_handle: currentRacket.handle,
          fit_result: assessment
        });

        host.querySelector("#fitContinue").addEventListener("click", () => {
          track("tps_fit_check_current_confirmed", {
            product_handle: currentRacket.handle,
            fit_result: assessment
          });
          closeDialog();
        });

        host.querySelector("#fitRestart").addEventListener("click", () => {
          currentQuestion = 0;
          answers = {};
          renderQuestion();
        });

        host.querySelectorAll("[data-alt-handle]").forEach(link => {
          link.addEventListener("click", () => {
            track("tps_fit_check_alternative_clicked", {
              product_handle: currentRacket.handle,
              alternative_handle: link.dataset.altHandle,
              fit_result: assessment
            });
          });
        });
      };

      openButton.addEventListener("click", openDialog);
      closeButton.addEventListener("click", closeDialog);
      dialog.addEventListener("click", event => {
        if (event.target === dialog) closeDialog();
      });
      backButton.addEventListener("click", () => {
        if (completed) {
          currentQuestion = questions.length - 1;
          renderQuestion();
        } else if (currentQuestion > 0) {
          currentQuestion -= 1;
          renderQuestion();
        }
      });

      renderQuestion();
    }
  }

  if (!customElements.get("the-padel-shop-fit-check")) {
    customElements.define("the-padel-shop-fit-check", ThePadelShopFitCheck);
  }
})();